import { useSearchParams } from "react-router-dom";
import { Alert } from "@allaria-tech/matter/Alert";
import { Button } from "@allaria-tech/matter/Button";
import { Feedback } from "@allaria-tech/matter/Feedback";
import { Typography } from "@allaria-tech/matter/Typography";
import Container from "../Container/Container";
import usePartnershipRedirect from "../../hooks/usePartnershipRedirect";
import { navigateToAllowedUrl } from "../../utils";
import { getCompanyData, getPartnership } from "../../utils/companies";
import Layout from "../Layout/Layout";

import styles from "./SuccessPage.module.scss";
import {
  success,
  successNonAllaria,
} from "../../i18n/securities/preconfirmationStep";
import { isAllariaOnboarding } from "../../utils";

const SuccessPage = () => {
  const {
    successContainer,
    body,
    transparentCard,
    pendingAlert,
    redirectMessage,
  } = styles;

  const [searchParams] = useSearchParams();
  const language = (searchParams.get("language") as "es" | "en") || "es";
  const signatories = searchParams.get("signatories");
  const coowners = searchParams.get("coowners");

  // Allaria Fondos: the company already had an Allaria account and only
  // requested the "cuotapartista" account opening
  const isCuotapartistaRequest = searchParams.get("cuotapartista") === "true";

  const getSuccessSubtitle = () => {
    if (isCuotapartistaRequest)
      return "Procesaremos tu solicitud y nos pondremos en contacto para darte instrucciones para acceder a tu cuenta.";
    if (isAllariaOnboarding())
      return "Procesaremos tu solicitud y recibirás un mail a la brevedad con las instrucciones para completar la creación de tu cuenta.";
    if (signatories || coowners)
      return `Procesaremos tu solicitud y enviaremos un mail a cada ${signatories ? "firmante" : "cotitular"} con las instrucciones para su registro.`;
    return "En breve nos estaremos contactando para informarte sobre el avance del proceso.";
  };

  const successSubtitle = getSuccessSubtitle();

  const companyData = getCompanyData();
  const { companyName, partnerships } = companyData;

  const isAllariaSecurity = companyName === "ALLARIA_SECURITY";
  const hasPendingParties = Boolean(signatories || coowners);
  const isOpeningInProgress = !isAllariaSecurity && hasPendingParties;

  const title = isAllariaSecurity
    ? success[language].title
    : isCuotapartistaRequest
      ? "¡Solicitaste la apertura de cuenta cuotapartista!"
      : isOpeningInProgress
        ? successNonAllaria[language].inProgressTitle
        : successNonAllaria[language].completedTitle;

  const description = isAllariaSecurity
    ? success[language].description
    : successSubtitle;

  // Resolve active partnership from the URL query params.
  const activePartnership =
    partnerships?.find((p) => searchParams.has(p.key.replace(/\+$/, ""))) ??
    getPartnership();

  // Only shows the redirect UI when the partnership opts in, the caller provided
  // a target URL, and that URL passes the domain whitelist.
  const { redirect, secondsLeft } = usePartnershipRedirect({
    config: activePartnership?.successRedirect,
  });

  return (
    <Layout
      centeredLogo
      hideStepper
      hideSupportButton
      layoutClassName="h-screen"
    >
      <Container className={successContainer}>
        <div className={body}>
          <Feedback
            inline
            className={transparentCard}
            type={isOpeningInProgress ? "PENDING" : "SUCCESS"}
            title={title}
            description={description}
          />
          {isOpeningInProgress && (
            <Alert
              className={pendingAlert}
              variant="INFO"
              title={`La cuenta se abrirá cuando todos los 
                  ${signatories ? "firmantes" : "cotitulares"} se registren.`}
              description="Recibirán las instrucciones por mail para acceder a la cuenta."
            />
          )}
          {redirect && (
            <>
              <Typography
                type="BODY"
                size="S"
                className={redirectMessage}
                color="var(--typography-body-color-base)"
              >
                {redirect.message(secondsLeft)}
              </Typography>
              <Button
                text={redirect.buttonText}
                variant="primary"
                onClick={() =>
                  navigateToAllowedUrl(redirect.url, redirect.allowedDomains)
                }
              />
            </>
          )}
        </div>
      </Container>
    </Layout>
  );
};

export default SuccessPage;
